class StoreStatus {
  constructor(options = {}) {
    this.options = {
      selector: '.js-wait-time', // Default selector for wait time elements
      filter: () => true, // Default filter function
      ...options
    };
  }

  // Toggle this to true for testing
  static TEST_MODE = false;
  getNow() {
    if (StoreStatus.TEST_MODE) {
      const now = new Date();
      now.setHours(16, 30, 0, 0);
      return now;
    }
    return new Date();
  }

  async fetchShopStatus(storeId) {
    try {
      const response = await fetch(`https://gft.tommyguns.com/api/v1/kiosk/GetShopStatus/${storeId}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      // TESTING: Emulate all locations as closed
      if (StoreStatus.TEST_MODE) {
        data.shopStatus = 'Open';
      }
      return data;
    } catch (error) {
      return {
        shopStatus: 'Error',
        waitTime: 0,
        upcomingOpenTime: ''
      };
    }
  }

  async fetchShopStatuses(storeIds) {
    // Fetch all store statuses in parallel
    const promises = storeIds.map(storeId => this.fetchShopStatus(storeId));
    const results = await Promise.all(promises);

    // Convert array of results to object with storeId as key
    return storeIds.reduce((acc, storeId, index) => {
      acc[storeId] = results[index];
      return acc;
    }, {});
  }

  formatWaitTime(waitTime) {
    if (waitTime === 0) {
      return {
        text: '',
        time: 'Serving Now'
      };
    }

    const time = new Number(waitTime);
    const hours = Math.floor(time / 60);
    const minutes = time % 60;

    if (hours > 0) {
      return {
        text: 'Estimated',
        time: `${hours} Hr ${minutes} Min Wait`
      };
    } else {
      return {
        text: 'Estimated',
        time: `${minutes} Minute Wait`
      };
    }
  }

  getWaitTimeContent(data) {
    const { waitTime, shopStatus, upcomingOpenTime = '' } = data;
    let textContent, timeContent;

    switch (shopStatus) {
      case 'Open':
        const formatted = this.formatWaitTime(waitTime);
        textContent = formatted.text;
        timeContent = formatted.time;
        break;
      case 'Closed':
        textContent = upcomingOpenTime || '';
        timeContent = 'Shop is Closed';
        break;
      case 'Full':
        textContent = upcomingOpenTime || '';
        timeContent = 'Full for the day';
        break;
      case 'Error':
        textContent = '';
        timeContent = 'Please Call to Book';
        break;
      default:
        textContent = '';
        timeContent = 'Please Call to Book';
    }

    return { textContent, timeContent };
  }

  // Helper to format time in 12-hour format
  formatTime12h(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours < 12 ? 'am' : 'pm';
    const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
    return `${formattedHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  // Helper to get formatted opening hours text
  getHoursText(dayHours) {
    if (dayHours.is_closed || !dayHours.open_time || !dayHours.close_time) {
      return 'Closed';
    }
    const openTime = this.formatTime12h(dayHours.open_time);
    const closeTime = this.formatTime12h(dayHours.close_time);
    return `${openTime} - ${closeTime}`;
  }

  // Helper to get close time if the store is currently open
  getCloseTimeIfOpen(hours) {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const todayHours = hours.openHours[today];
    if (todayHours && !todayHours.is_closed) {
      const now = this.getNow();
      const [openHour, openMinute] = todayHours.open_time.split(':').map(Number);
      const [closeHour, closeMinute] = todayHours.close_time.split(':').map(Number);
      const openTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), openHour, openMinute);
      const closeTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), closeHour, closeMinute);
      if (now >= openTime && now <= closeTime) {
        return this.formatTime12h(todayHours.close_time);
      }
    }
    return null;
  }

  // Helper to get the next opening time if the store is closed
  getNextOpeningTime(hours) {
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = this.getNow();
    const todayIndex = today.getDay();
    let dayIndex = todayIndex;
    while (true) {
      const dayHours = hours.openHours[daysOfWeek[dayIndex]];
      if (dayHours && !dayHours.is_closed) {
        const now = this.getNow();
        const [openHour, openMinute] = dayHours.open_time.split(':').map(Number);
        const openTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), openHour, openMinute);
        if (dayIndex === todayIndex && now < openTime) {
          return `Opens at ${this.formatTime12h(dayHours.open_time)}`;
        } else if (dayIndex !== todayIndex) {
          const dayName = daysOfWeek[dayIndex].charAt(0).toUpperCase() + daysOfWeek[dayIndex].slice(1);
          return `Opens ${dayName} at ${this.formatTime12h(dayHours.open_time)}`;
        }
      }
      dayIndex = (dayIndex + 1) % 7;
      if (dayIndex === todayIndex) break;
    }
    return 'Shop Closed';
  }

  // Modular method to update store hours
  updateStoreHours(container, storeData) {
    if (!storeData?.hours) return;

    const { hours } = storeData;
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const shopHoursSections = document.querySelectorAll(`.shop-hours`);
    if (!shopHoursSections.length) return;

    shopHoursSections.forEach(section => {
      const weekHours = section.querySelector('.week-hours');
      const todayHours = section.querySelector('.today-hours');
      const shopStatus = section.querySelector('.shop-status');

      if (!weekHours || !todayHours || !shopStatus) return;

      // Clear and setup week hours container
      weekHours.innerHTML = `
        <div class="gft-hoop">
          ${Object.entries(hours.openHours).map(([day, dayHours]) => `
            <div class="gft-wrap">
              <p class="gft-day">${day.charAt(0).toUpperCase() + day.slice(1)}</p>
              <p class="gft-hours">${this.getHoursText(dayHours)}</p>
            </div>
          `).join('')}
        </div>
      `;

      // Update today's hours
      todayHours.textContent = this.getHoursText(hours.openHours[today]);

      // Update shop status
      const closeTime = this.getCloseTimeIfOpen(hours);
      shopStatus.textContent = closeTime
        ? `Open until ${closeTime}`
        : this.getNextOpeningTime(hours);
    });
  }

  updateWaitTime(container, storeData) {
    if (storeData) {
      // Add loading class to start fade out
      container.classList.add('loading-wait-times');

      // Listen for the fade out transition to complete
      const handleTransitionEnd = () => {
        // Remove the event listener
        container.removeEventListener('transitionend', handleTransitionEnd);

        // Get the wait time content
        const { textContent, timeContent } = this.getWaitTimeContent(storeData);

        // Update the text content of existing elements
        const textElement = container.querySelector('.wait-time__text');
        const timeElement = container.querySelector('.wait-time__time');

        if (textElement) {
          textElement.textContent = textContent;
          textElement.classList.remove('wait-time__text__placeholder');
          // Add reopens class if it's a reopening message
          textElement.classList.toggle('wait-time__text--reopens',
            ['Closed', 'Full'].includes(storeData.shopStatus) && textContent);
        }

        if (timeElement) {
          timeElement.textContent = timeContent;
          timeElement.classList.remove('wait-time__time__placeholder');
        }

        // Wait for next frame to ensure new content is rendered
        requestAnimationFrame(() => {
          // Remove loading class to fade in
          container.classList.remove('loading-wait-times');
        });
      };

      // Add the transition end listener
      container.addEventListener('transitionend', handleTransitionEnd);

      // Update check-in button visibility
      const checkInButton = container.closest('.location__actions')?.querySelector('.btn--checkin.js-check-in');
      if (checkInButton) {
        const isClosed = ['Closed', 'Full'].includes(storeData.shopStatus);
        checkInButton.style.display = isClosed ? 'none' : 'block';
        checkInButton.classList.toggle('shop-closed', storeData.shopStatus === 'Closed');
        checkInButton.classList.toggle('shop-full', storeData.shopStatus === 'Full');
      }
      // Toggle location-open on .check-in-wrapper if store is open
      const checkInWrapper = container.closest('.check-in-wrapper');
      if (checkInWrapper) {
        const isOpen = storeData.shopStatus === 'Open';
        checkInWrapper.classList.toggle('location-open', isOpen);
      }
    }
  }

  async init(scope = document) {
    const waitTimeElements = scope.querySelectorAll(this.options.selector);

    if (waitTimeElements.length === 0) {
      return;
    }

    // Collect all store IDs, applying the filter
    const storeIds = Array.from(waitTimeElements)
      .map(element => {
        const locationElement = element.closest('.has-wait-time');
        const storeId = locationElement ? locationElement.getAttribute('data-shortcut-id') : null;
        return storeId;
      })
      .filter(storeId => {
        const result = storeId && this.options.filter(storeId);
        return result;
      });

    if (storeIds.length === 0) {
      return;
    }

    // Fetch all store statuses in parallel
    const storeStatuses = await this.fetchShopStatuses(storeIds);
    if (!storeStatuses) {
      return;
    }

    // Update each wait time element with its corresponding store data
    waitTimeElements.forEach(element => {
      const locationElement = element.closest('.has-wait-time');
      const storeId = locationElement ? locationElement.getAttribute('data-shortcut-id') : null;
      if (storeId && this.options.filter(storeId)) {
        const storeData = storeStatuses[storeId];
        if (storeData) {
          this.updateWaitTime(element, storeData);
        } else {
          console.warn('[StoreStatus] No data found for store', storeId);
        }
      }
    });
  }

  // Helper method to create a new instance with specific options
  static create(options) {
    return new StoreStatus(options);
  }
}

// Unified custom event for shop data (wait time and/or hours)
document.addEventListener('dispatch:shop-data', async function (e) {
  const {
    hours = false,
    waitTimes = false,
    wrapper = null,
    filter = null
  } = e.detail || {};

  // If neither hours nor wait times are requested, do nothing
  if (!hours && !waitTimes) return;

  const instance = StoreStatus.create();
  let storeId = filter;

  // If no store ID provided, try to get it from the wrapper
  if (!storeId) {
    const waitTimeContainer = wrapper.querySelector('.has-wait-time');
    storeId = waitTimeContainer?.getAttribute('data-shortcut-id');
    if (!storeId) return;
  }

  // Fetch store data once
  const storeData = await instance.fetchShopStatus(storeId);
  if (!storeData) return;

  // console.log('storeData: ', storeData);

  // Update wait times if requested
  if (waitTimes) {
    const waitTimeContainers = wrapper.querySelectorAll('.has-wait-time');
    waitTimeContainers.forEach(container => {
      const waitTimeElement = container.querySelector('.js-wait-time');
      if (waitTimeElement) {
        instance.updateWaitTime(waitTimeElement, storeData);
      }
    });
  }

  // Update hours if requested
  if (hours) {
    const shopHoursElements = wrapper.querySelectorAll('.shop-hours');
    shopHoursElements.forEach(element => {
      instance.updateStoreHours(element, storeData);
    });
  }
});
